import {getMainOrgUrl, urlState} from 'app/client/models/gristUrlState';
import {EmailVerifiedPage} from 'app/client/ui/EmailVerifiedPage';
import {ForgotPasswordPage} from 'app/client/ui/ForgotPasswordPage';
import {LoginPage} from 'app/client/ui/LoginPage';
import {SignupPage} from 'app/client/ui/SignupPage';
import {getPageTitleSuffix} from 'app/common/gristUrls';
import {LoginAPIImpl} from 'app/common/LoginAPI';
import {getGristConfig} from 'app/common/urlUtils';
import {Computed, Disposable, dom, DomContents, subscribe} from 'grainjs';

export class LoginPages extends Disposable {
  private readonly _currentPage = Computed.create(this, urlState().state, (_use, s) => s.login);
  private readonly _loginAPI = new LoginAPIImpl(getMainOrgUrl());

  constructor() {
    super();
    this._setPageTitle();
  }

  public buildDom() {
    return dom.domComputed(this._currentPage, (page): DomContents => {
      switch (page) {
        case undefined:
        case 'signup': {
          return dom.create(SignupPage, this._loginAPI);
        }
        case 'login': {
          return dom.create(LoginPage, this._loginAPI);
        }
        case 'verified': {
          return dom.create(EmailVerifiedPage);
        }
        case 'forgot-password': {
          return dom.create(ForgotPasswordPage, this._loginAPI);
        }
        default: {
          // Make sure we don't forget to handle a page. Leaving out this default
          // case is typically enough for TypeScript to catch such errors, but
          // since `undefined` is a valid `DomContents` value and is implicitly
          // returned in the absence of a default case, we need to explicitly
          // check that the type of `page` is `never`.
          const exhaustiveCheck: never = page;
          throw new Error(`Unhandled page: ${exhaustiveCheck}`);
        }
      }
    });
  }

  private _setPageTitle() {
    this.autoDispose(subscribe(this._currentPage, (_use, page): string => {
      const suffix = getPageTitleSuffix(getGristConfig());
      switch (page) {
        case undefined:
        case 'signup': {
          return document.title = `Sign up${suffix}`;
        }
        case 'forgot-password':
        case 'login': {
          return document.title = `Sign in${suffix}`;
        }
        case 'verified': {
          return document.title = `Email verified${suffix}`;
        }
      }
    }));
  }
}
